import { useState } from "react";
import { toast } from "sonner";
import api, { apiError, brl, maskDoc, printAgent } from "@/lib/api";
import { buildReceiptHtml, receiptFromSale } from "@/pages/seller/pos/receipt";
import { Ban, CreditCard, FileText, Printer, Receipt, UserCog, UserSquare } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import Autocomplete from "@/components/panel/Autocomplete";
import { Button, Field, Input, Textarea } from "@/components/ui-kit";

/**
 * Diálogos da VENDA concluída, extraídos da antiga tela de Vendas.
 *
 * Saíram de dentro dela quando Vendas e Orçamentos viraram uma lista só: os
 * mesmos diálogos passaram a ser abertos a partir de uma linha que pode ser
 * venda, orçamento ou venda em aberto, e mantê-los presos à tela antiga
 * significaria duplicá-los.
 */

export const METHOD_LABEL = {
  dinheiro: "Dinheiro", pix: "PIX", cartao_debito: "Débito", cartao_credito: "Crédito", misto: "Misto",
};

export const dt = (iso) => new Date(iso).toLocaleString("pt-BR", {
  day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
});

export const isCancelled = (s) => s.status === "cancelada";

/**
 * Confirmação do cancelamento.
 *
 * Não usa o `ConfirmDialog` genérico porque aqui o motivo é parte da decisão: é
 * o que o dono vai ler daqui a três meses para entender por que aquele dia
 * fechou diferente. O texto lista o que a ação faz — estorno de estoque e saída
 * do faturamento são consequências que ninguém deveria descobrir depois.
 */
export function CancelSaleDialog({ sale, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/sales/${sale.id}/cancel`, { reason: reason.trim() || null });
      toast.success(`Venda ${sale.number} cancelada. Estoque estornado.`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Cancelar a venda ${sale.number}?`} testId="cancel-sale-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">
        Os {(sale.items || []).reduce((n, i) => n + i.qty, 0)} item(ns) voltam para o estoque físico
        e os {brl(sale.total)} saem do faturamento, das comissões e do caixa. A venda continua na
        lista, marcada como cancelada.
      </p>
      <div className="mt-4">
        <Field label="Motivo" labelTone="plain" hint="Aparece na venda e no extrato do caixa.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="Ex.: cliente desistiu da compra" data-testid="cancel-sale-reason" />
        </Field>
      </div>
      <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Button variant="secondary" onClick={onClose} data-testid="cancel-sale-back">Voltar</Button>
        <Button variant="danger" icon={Ban} loading={saving} onClick={confirm} data-testid="cancel-sale-confirm">
          Cancelar venda
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Troca do vendedor da venda.
 *
 * A tela mostra a comissão SAINDO de um e ENTRANDO no outro, com os dois valores
 * lado a lado, antes de confirmar. Sem isso a operação parece só "corrigir um
 * nome" — e não é: a comissão é recalculada na taxa do novo vendedor, então o
 * valor muda quando as taxas diferem, e quem confirma precisa ver isso antes.
 */
export function ChangeSellerDialog({ sale, sellers, onClose, onDone }) {
  const [sellerId, setSellerId] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [saving, setSaving] = useState(false);

  // Só quem ainda atende: vendedor desativado não pode receber comissão, e o
  // servidor recusa. Oferecer na lista seria convidar ao erro.
  const opcoes = sellers.filter((s) => s.active && s.id !== sale.seller_id);
  const escolhido = sellers.find((s) => s.id === sellerId);
  // Mesma conta do servidor (total × taxa). É previsão, não promessa: quem grava
  // o valor é o backend.
  const novaComissao = escolhido ? (sale.total * (Number(escolhido.commission_rate) || 0)) / 100 : null;

  const confirm = async () => {
    if (!sellerId) return;
    setSaving(true);
    try {
      await api.post(`/admin/sales/${sale.id}/seller`, { seller_id: sellerId });
      toast.success(`Venda ${sale.number} agora é de ${sellerName}.`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Trocar o vendedor da venda ${sale.number}`} testId="change-seller-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">
        A comissão sai de <b>{sale.seller_name || "—"}</b> e passa para quem você escolher,
        recalculada pela <b>taxa do novo vendedor</b>. A venda, o total e o estoque não mudam.
      </p>

      <Field label="Novo vendedor" className="mt-4">
        <Autocomplete
          value={sellerId}
          onChange={(v, l) => { setSellerId(v); setSellerName(l); }}
          options={opcoes.map((s) => ({ value: s.id, label: s.name }))}
          selectedLabel={sellerName}
          ariaLabel="Escolher o novo vendedor"
          placeholder="Escolha quem atendeu" testId="change-seller-picker" width="w-full" />
      </Field>

      {escolhido && (
        <div className="mt-4 grid grid-cols-2 gap-2" data-testid="change-seller-preview">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Sai de</p>
            <p className="text-sm font-medium text-slate-700 truncate">{sale.seller_name || "—"}</p>
            <p className="font-mono text-sm text-red-500 mt-0.5">− {brl(sale.commission)}</p>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wider text-emerald-700">Vai para</p>
            <p className="text-sm font-medium text-emerald-900 truncate">{escolhido.name}</p>
            <p className="font-mono text-sm text-emerald-700 mt-0.5">
              + {brl(novaComissao)}
              <span className="text-emerald-600/70 font-sans"> ({escolhido.commission_rate}%)</span>
            </p>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Button variant="secondary" onClick={onClose} data-testid="change-seller-back">Voltar</Button>
        <Button icon={UserCog} loading={saving} disabled={!sellerId} onClick={confirm} data-testid="change-seller-confirm">
          Passar a venda
        </Button>
      </div>
    </Modal>
  );
}

/** Detalhe da venda: itens e formas de pagamento já vêm na listagem. */
const METODOS = [
  ["dinheiro", "Dinheiro"], ["pix", "PIX"],
  ["cartao_debito", "Débito"], ["cartao_credito", "Crédito"],
];

/**
 * Troca o cliente da venda.
 *
 * O caso real: a venda saiu como "Consumidor Final" e o cliente volta pedindo a
 * nota no CPF dele, ou o vendedor vinculou a pessoa errada. Sem isto, a única
 * saída era cancelar a venda — estornando estoque e queimando número — para
 * relançar tudo igual com outro nome.
 *
 * Aceita cadastro OU nome/documento digitado: o balcão atende os dois casos, e o
 * cliente que só quer a nota no CPF é a maioria.
 */
export function ChangeCustomerDialog({ sale, onClose, onDone }) {
  const [nome, setNome] = useState(sale.customer_name === "Consumidor Final" ? "" : (sale.customer_name || ""));
  const [doc, setDoc] = useState(sale.customer_document ? maskDoc(sale.customer_document) : "");
  const [saving, setSaving] = useState(false);

  // Espelha a regra do servidor (PosSaleService::changeCustomer): o
  // destinatário está dentro da nota autorizada.
  const temNota = !!sale.invoice
    && !["cancelada", "erro_autorizacao", "denegada"].includes(sale.invoice.status);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/sales/${sale.id}/customer`, {
        customer_name: nome.trim() || undefined,
        customer_document: doc.replace(/\D/g, "") || undefined,
      });
      toast.success(nome.trim()
        ? `Venda ${sale.number} agora está no nome de ${nome.trim()}.`
        : `Venda ${sale.number} voltou para Consumidor Final.`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Trocar o cliente da venda ${sale.number}`} testId="change-customer-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">
        O valor de <b className="font-mono">{brl(sale.total)}</b>, o estoque e a comissão não mudam.
        Muda só a quem a venda pertence — e é esse nome que vai para a nota quando ela for emitida.
      </p>

      {temNota && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5" data-testid="change-customer-blocked">
          <p className="text-sm text-red-700">
            Esta venda tem a nota <b className="font-mono">{sale.invoice.number}</b> emitida, e o destinatário
            já está declarado na SEFAZ. Cancele a nota em Fiscal antes de trocar o cliente.
          </p>
        </div>
      )}

      <fieldset className="mt-4 space-y-3" disabled={temNota}>
        <Field label="Nome do cliente" hint="Em branco: a venda volta para Consumidor Final.">
          <Input value={nome} onChange={(e) => setNome(e.target.value)}
            placeholder="Nome de quem levou" data-testid="change-customer-name" />
        </Field>
        <Field label="CPF / CNPJ" hint="Obrigatório se for emitir NF-e.">
          <Input value={doc} onChange={(e) => setDoc(maskDoc(e.target.value))}
            data-testid="change-customer-doc" mono />
        </Field>
      </fieldset>

      <div className="mt-5 flex flex-col-reverse gap-2.5 sm:flex-row sm:gap-3 sm:justify-end">
        <Button variant="secondary" onClick={onClose}>Voltar</Button>
        <Button onClick={confirm} loading={saving} disabled={temNota} data-testid="change-customer-confirm">
          Salvar cliente
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Emite a nota da venda pelo detalhe, sem passar pelo PDV.
 *
 * A venda de balcão emite NFC-e sozinha conforme a configuração da loja, mas
 * sobra sempre um caso: o cliente que pede NF-e depois, ou a forma de pagamento
 * que não está na emissão automática. Antes isso só existia na tela do PDV, e
 * só para a última venda — uma venda de ontem não tinha caminho nenhum.
 */
export function EmitInvoiceDialog({ sale, onClose, onDone }) {
  const [doc, setDoc] = useState(sale.customer_document ? maskDoc(sale.customer_document) : "");
  const [obs, setObs] = useState("");
  const [emitindo, setEmitindo] = useState(null);   // 'nfce' | 'nfe'

  /* O bloqueio do servidor é POR TIPO: ter NFC-e não impede a NF-e. É o caso
     real do balcão — o cliente leva o cupom e depois pede a nota no CNPJ da
     empresa dele. A tela precisa refletir isso, senão esconde uma emissão que
     seria aceita. */
  const jaTem = (tipo) => (sale.invoices || []).some(
    (i) => i.type === tipo && ["autorizada", "processando"].includes(i.status),
  );

  const emitir = async (type) => {
    if (type === "nfe" && doc.replace(/\D/g, "").length < 11) {
      toast.error("A NF-e exige CPF ou CNPJ do destinatário.");
      return;
    }
    setEmitindo(type);
    try {
      const { data } = await api.post("/pos/invoices/emit", {
        pos_sale_id: sale.id,
        type,
        customer_document: doc.replace(/\D/g, "") || undefined,
        informacoes_complementares: obs.trim() || undefined,
      });
      // A emissão é assíncrona: a SEFAZ pode recusar já na resposta. Sem checar,
      // a tela diria "emitida" para uma nota que falhou.
      if (["erro_autorizacao", "rejeitada", "denegada", "erro"].includes(data.status)) {
        toast.error(data.status_message || "A SEFAZ recusou a nota.", { duration: 15000 });
      } else {
        toast.success(`${type === "nfe" ? "NF-e" : "NFC-e"} ${data.number} enviada para autorização.`);
      }
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEmitindo(null); }
  };

  return (
    <Modal open onClose={onClose} title={`Emitir nota da venda ${sale.number}`} testId="emit-sale-invoice-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">
        Total de <b className="font-mono">{brl(sale.total)}</b> para <b>{sale.customer_name}</b>.
      </p>

      <Field label="CPF / CNPJ do destinatário" className="mt-4"
        hint="A NFC-e sai sem documento (consumidor não identificado); a NF-e, não.">
        <Input value={doc} onChange={(e) => setDoc(maskDoc(e.target.value))} data-testid="emit-sale-doc" mono />
      </Field>

      {/* Quadro "Dados adicionais" da DANFE. Texto livre: número do pedido,
          condição de pagamento, prazo de garantia, observação da entrega. */}
      <Field label="Dados adicionais" className="mt-3"
        hint={`Aparece no quadro "Informações adicionais" da nota. ${500 - obs.length} caracteres restantes.`}>
        <Textarea value={obs} onChange={(e) => setObs(e.target.value.slice(0, 500))} rows={2}
          placeholder="Ex.: Garantia de 12 meses. Pedido 4821." data-testid="emit-sale-obs" />
      </Field>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Button variant="secondary" icon={Receipt}
          loading={emitindo === "nfce"} disabled={!!emitindo || jaTem("nfce")}
          onClick={() => emitir("nfce")} data-testid="emit-sale-nfce">
          {jaTem("nfce") ? "NFC-e já emitida" : "Cupom fiscal (NFC-e)"}
        </Button>
        <Button icon={FileText}
          loading={emitindo === "nfe"} disabled={!!emitindo || jaTem("nfe")}
          onClick={() => emitir("nfe")} data-testid="emit-sale-nfe">
          {jaTem("nfe") ? "NF-e já emitida" : "Nota fiscal (NF-e)"}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Corrige POR ONDE o dinheiro entrou — sem mexer no valor.
 *
 * O caso real: o vendedor bate "Dinheiro" e o cliente paga no débito. Antes, a
 * única saída era cancelar a venda (estornando estoque e queimando número de
 * série) ou deixar errado — e o caixa fechava com sobra numa forma e falta em
 * outra, todo dia, sem ninguém saber de onde vinha.
 *
 * Com nota emitida o botão nem chega aqui habilitado: a forma consta na NFC-e
 * autorizada, e o servidor recusa. O caminho nesse caso é cancelar a nota.
 */
export function ChangePaymentDialog({ sale, onClose, onDone }) {
  const [method, setMethod] = useState(sale.payment_method);
  const [installments, setInstallments] = useState(1);
  const [saving, setSaving] = useState(false);

  // Espelha a regra do servidor (SaleAdminController::changePaymentMethod):
  // só nota cancelada, com erro de autorização ou denegada deixam corrigir.
  const temNota = !!sale.invoice
    && !["cancelada", "erro_autorizacao", "denegada"].includes(sale.invoice.status);
  const mudou = method !== sale.payment_method;

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/sales/${sale.id}/payment-method`, {
        payment_method: method,
        installments: method === "cartao_credito" ? parseInt(installments) || 1 : undefined,
      });
      toast.success(`Venda ${sale.number} agora consta como ${METODOS.find(([v]) => v === method)?.[1]}.`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Corrigir o pagamento da venda ${sale.number}`} testId="change-payment-dialog">
      <p className="text-sm text-slate-600 leading-relaxed">
        O valor de <b className="font-mono">{brl(sale.total)}</b>, o estoque e a comissão não mudam.
        Muda só a forma registrada — é ela que o fechamento de caixa confere contra a gaveta e a maquininha.
      </p>

      {temNota && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5" data-testid="change-payment-blocked">
          <p className="text-sm text-red-700">
            Esta venda tem a nota <b className="font-mono">{sale.invoice.number}</b> emitida, e a forma de
            pagamento consta nela. Cancele a nota em Fiscal antes de corrigir.
          </p>
        </div>
      )}

      <fieldset className="mt-4" disabled={temNota}>
        <legend className="text-eyebrow uppercase tracking-wider text-slate-500">Forma correta</legend>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {METODOS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`change-payment-${v}`}
              aria-pressed={method === v} disabled={temNota}
              className={`text-sm font-semibold px-3 h-9 rounded-lg border disabled:opacity-40 ${
                method === v ? "bg-brand-600 border-brand-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
        {method === "cartao_credito" && (
          <Input type="number" min="1" max="24" value={installments} onChange={(e) => setInstallments(e.target.value)}
            data-testid="change-payment-installments" placeholder="Parcelas" aria-label="Número de parcelas"
            mono className="mt-2 w-28" />
        )}
      </fieldset>

      {/* Uma venda dividida em várias formas volta a ser UMA. Dizer isso antes
          evita a descoberta depois, no extrato do caixa. */}
      {mudou && (sale.payments || []).length > 1 && (
        <p className="text-xs text-amber-700 mt-3" data-testid="change-payment-merge-warning">
          Esta venda estava dividida em {sale.payments.length} formas. A correção deixa tudo em uma só.
        </p>
      )}

      <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Button variant="secondary" onClick={onClose} data-testid="change-payment-back">Voltar</Button>
        <Button icon={CreditCard} loading={saving} disabled={!mudou || temNota} onClick={confirm}
          data-testid="change-payment-confirm">
          Corrigir pagamento
        </Button>
      </div>
    </Modal>
  );
}

export function SaleDetail({ sale, onClose, onCancel, onChangeSeller, onChangePayment, onChangeCustomer, onEmitInvoice }) {
  const cancelled = isCancelled(sale);
  const [imprimindo, setImprimindo] = useState(null);

  /* Notas que EXISTEM de verdade. Tentativa recusada não conta: é justamente
     quando reemitir (e corrigir o cliente antes) precisa continuar possível. */
  const notasValidas = (sale.invoices || []).filter(
    (i) => ["autorizada", "processando"].includes(i.status),
  );
  const temNota = notasValidas.length > 0;
  // O bloqueio de emissão é POR TIPO no servidor: ter NFC-e não impede a NF-e.
  const faltaAlgumTipo = ["nfce", "nfe"].some((t) => !notasValidas.some((i) => i.type === t));

  /** Cupom NÃO fiscal: o comprovante de balcão, na bobina. Vale para qualquer venda. */
  const imprimirCupom = async () => {
    setImprimindo("cupom");
    try {
      await printAgent.printReceipt(buildReceiptHtml(receiptFromSale(sale)), { label: `Cupom ${sale.number}` });
      toast.success("Cupom enviado para impressão");
    } catch (e) { toast.error(e.message || "Falha ao imprimir"); }
    finally { setImprimindo(null); }
  };

  /** Reimprime o documento FISCAL. O papel é decidido pelo tipo, não pelo operador. */
  const reimprimirNota = async (nota) => {
    setImprimindo(nota.id);
    try {
      await printAgent.printApiDoc(`/invoices/${nota.id}/${nota.type === "nfce" ? "cupom" : "pdf"}`);
      toast.success(`${nota.type === "nfce" ? "NFC-e" : "NF-e"} ${nota.number} enviada para impressão`);
    } catch (e) { toast.error(e.message || "Falha ao imprimir"); }
    finally { setImprimindo(null); }
  };

  return (
    <Modal open onClose={onClose} title={`Venda ${sale.number}`} maxWidth="max-w-lg" testId="sale-detail-dialog">
      <div className="space-y-4">
        {cancelled && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5" data-testid="sale-detail-cancelled">
            <p className="text-sm font-semibold text-red-700">Venda cancelada</p>
            <p className="text-xs text-red-600 mt-0.5">
              {sale.cancelled_at ? dt(sale.cancelled_at) : "—"}
              {sale.cancelled_by_name ? ` · por ${sale.cancelled_by_name}` : ""}
            </p>
            {sale.cancel_reason && <p className="text-sm text-red-700 mt-1.5">{sale.cancel_reason}</p>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">Cliente</span><p className="text-slate-900">{sale.customer_name}{sale.customer_document ? ` · ${maskDoc(sale.customer_document)}` : ""}</p></div>
          <div><span className="text-slate-500">Vendedor</span><p className="text-slate-900">{sale.seller_name || "—"}</p></div>
          <div><span className="text-slate-500">Quando</span><p className="text-slate-900">{dt(sale.created_at)}</p></div>
          <div><span className="text-slate-500">Comissão</span><p className="font-mono text-slate-900">{brl(sale.commission)}</p></div>
        </div>

        {/* Venda que o balcão fechou sem conexão e que subiu depois.
            É o que explica a diferença entre a hora do cupom e a hora do
            registro: "Quando", logo acima, é o instante em que o servidor gravou
            — não o do atendimento. Sem esta linha, a diferença parece erro. */}
        {sale.origin === "offline" && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800"
            data-testid="sale-detail-offline">
            Venda registrada sem conexão
            {sale.offline_created_at ? ` no balcão às ${dt(sale.offline_created_at)}` : ""}
            {" "}e enviada ao servidor quando a rede voltou.
          </div>
        )}

        {/* Rastro da troca de cliente. Mesma razão do rastro do vendedor: uma
            venda que muda de dono sem deixar sinal é impossível de explicar
            depois — e aqui o nome é o que vai para a nota. */}
        {sale.customer_changed_at && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
            data-testid="sale-detail-customer-changed">
            Cliente alterado em {dt(sale.customer_changed_at)}
            {sale.customer_changed_by_name ? ` por ${sale.customer_changed_by_name}` : ""}
            {sale.previous_customer_name ? ` — antes era ${sale.previous_customer_name}.` : "."}
          </div>
        )}

        {/* Rastro da troca: a comissão mudou de dono, e a venda precisa dizer
            isso sozinha. Sem esta linha, "essa venda era minha" não teria como
            ser resolvido três meses depois. */}
        {sale.seller_changed_at && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
            data-testid="sale-detail-seller-changed">
            Vendedor alterado em {dt(sale.seller_changed_at)}
            {sale.seller_changed_by_name ? ` por ${sale.seller_changed_by_name}` : ""}
            {sale.previous_seller_name ? ` — antes era de ${sale.previous_seller_name}.` : "."}
          </div>
        )}

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Itens</p>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {(sale.items || []).map((i) => (
              <div key={i.id} className="flex justify-between px-3 py-2 text-sm">
                <span className="text-slate-700">{i.qty}x {i.product_name}{i.variant_label ? ` (${i.variant_label})` : ""}</span>
                <span className="font-mono text-slate-900">{brl(i.unit_price * i.qty)}</span>
              </div>
            ))}
          </div>
        </div>

        {(sale.payments || []).length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Pagamento</p>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {sale.payments.map((p) => (
                <div key={p.id} className="flex justify-between px-3 py-2 text-sm">
                  <span className="text-slate-700">{METHOD_LABEL[p.method] || p.method}{p.installments > 1 ? ` ${p.installments}x` : ""}</span>
                  <span className="font-mono text-slate-900">{brl(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-slate-200 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{brl(sale.subtotal)}</span></div>
          {sale.discount > 0 && <div className="flex justify-between text-red-500"><span>Desconto</span><span className="font-mono">− {brl(sale.discount)}</span></div>}
          <div className={`flex justify-between font-semibold ${cancelled ? "text-slate-400" : "text-slate-900"}`}>
            <span>Total</span>
            <span className={`font-mono ${cancelled ? "line-through" : ""}`}>{brl(sale.total)}</span>
          </div>
        </div>

        {/* Rastro da correção de forma, pelo mesmo motivo do rastro de vendedor:
            é o número que o financeiro concilia com a maquininha, e "essa venda
            era em dinheiro" precisa ter resposta três meses depois. */}
        {sale.payment_changed_at && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
            data-testid="sale-detail-payment-changed">
            Forma de pagamento corrigida em {dt(sale.payment_changed_at)}
            {sale.payment_changed_by_name ? ` por ${sale.payment_changed_by_name}` : ""}
            {sale.previous_payment_method
              ? ` — antes era ${METHOD_LABEL[sale.previous_payment_method] || sale.previous_payment_method}.`
              : "."}
          </div>
        )}

        {/* IMPRESSÃO — separada das correções de propósito: imprimir não muda
            nada na venda, e misturá-la com cancelar e trocar vendedor colocaria
            uma ação inócua ao lado de outras que mexem em dinheiro. */}
        <div className="border-t border-slate-200 pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Imprimir</p>
          <div className="flex flex-wrap gap-2">
            {/* Toda venda tem cupom não fiscal — é o comprovante de balcão, e
                existe mesmo quando nota nenhuma foi emitida. */}
            <Button variant="secondary" icon={Printer} loading={imprimindo === "cupom"}
              onClick={imprimirCupom} data-testid="sale-detail-print-receipt">
              Cupom não fiscal
            </Button>
            {notasValidas.map((nota) => (
              <Button key={nota.id} variant="secondary"
                icon={nota.type === "nfce" ? Receipt : FileText}
                loading={imprimindo === nota.id}
                onClick={() => reimprimirNota(nota)} data-testid={`sale-detail-print-${nota.type}`}>
                {nota.type === "nfce" ? "NFC-e" : "NF-e"} {nota.number}
              </Button>
            ))}
          </div>
        </div>

        {!cancelled && (
          /* As duas correções à esquerda, cancelar à direita: são operações de
             peso muito diferente, e a destrutiva não deve ficar ao lado das
             corriqueiras como se fossem irmãs. */
          <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-2 justify-between">
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" icon={UserCog} onClick={onChangeSeller} data-testid="sale-detail-change-seller-btn">
                Trocar vendedor
              </Button>
              <Button variant="secondary" icon={CreditCard} onClick={onChangePayment} data-testid="sale-detail-change-payment-btn">
                Corrigir pagamento
              </Button>
              {/* Trocar cliente some com nota emitida: o destinatário já está
                  na SEFAZ, e o botão levaria a um diálogo que só sabe recusar. */}
              {!temNota && (
                <Button variant="secondary" icon={UserSquare} onClick={onChangeCustomer} data-testid="sale-detail-change-customer-btn">
                  Trocar cliente
                </Button>
              )}
              {/* Continua enquanto faltar ALGUM tipo: ter cupom fiscal não
                  impede a nota, e é o caso de quem compra no balcão e depois
                  pede NF-e no CNPJ. */}
              {faltaAlgumTipo && (
                <Button variant="secondary" icon={Receipt} onClick={onEmitInvoice} data-testid="sale-detail-emit-btn">
                  {temNota ? "Emitir outra nota" : "Emitir nota"}
                </Button>
              )}
            </div>
            <Button variant="dangerSoft" icon={Ban} onClick={onCancel} data-testid="sale-detail-cancel-btn">
              Cancelar venda
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
